/* eslint-disable jsx-a11y/click-events-have-key-events */
import moment from "moment";
import * as React from "react";
import { Box, Typography } from "@mui/material";

import { t } from "../../translate";
import classNames from "../../../helpers/classNames";
import * as calFns from "../functions/calendarFunctions";
import * as calendarData from "../functions/calendarData";

import styles from "./styles.module.css";

interface MonthViewProps {
  onDayClicked: (date: Date) => void;
  viewBsYear: number;
  viewBsMonth: number;
  defaultActiveDate: Date;
  maxValidDate?: Date;
  minValidDate?: Date;
  highlightedDays: Array<any>;
}

const MonthView: React.FC<MonthViewProps> = ({
  viewBsYear,
  onDayClicked,
  viewBsMonth,
  defaultActiveDate,
  maxValidDate,
  minValidDate,
  highlightedDays
}) => {
  const [, setSelectedDate] = React.useState(defaultActiveDate || new Date());

  const getDayInfo = (date: Date) => {
    const bsDate = calFns.convertADtoBS(date);
    return { adDate: new Date(date), ...bsDate };
  };

  const getDays = () => {
    const startDay = calFns.convertBStoAD(viewBsYear, viewBsMonth, 1);
    // Sunday, the first day in the view
    startDay.setDate(startDay.getDate() - startDay.getDay());

    const lastDay = calFns.convertBStoAD(
      viewBsYear,
      viewBsMonth,
      calFns.getBsMonthDays(viewBsYear, viewBsMonth)
    );
    // Saturday, the last day in the view
    lastDay.setDate(lastDay.getDate() + (6 - lastDay.getDay()));

    const days = [];
    while (startDay <= lastDay) {
      days.push(getDayInfo(startDay));
      startDay.setDate(startDay.getDate() + 1);
    }
    return days;
  };

  const isSameDate = (adDate: Date, toMatch: Date = new Date()) =>
    adDate.getDate() === toMatch.getDate() &&
    adDate.getMonth() === toMatch.getMonth() &&
    adDate.getFullYear() === toMatch.getFullYear();

  const isSameWeek = (date, daysHighlighted) => {
    // eslint-disable-next-line no-underscore-dangle
    const weekDays = daysHighlighted.map((item) => item._d.toString());
    return weekDays.includes(date.toString());
  };

  const isDateDisabled = (adDate: any) => {
    if (maxValidDate) {
      const disabled = moment(adDate).isAfter(moment(maxValidDate), "day");
      if (disabled) return disabled;
    }
    if (minValidDate) {
      const disabled = moment(adDate).isBefore(moment(minValidDate), "day");
      if (disabled) return disabled;
    }
    return false;
  };

  const onDaySelect = (date: Date) => {
    setSelectedDate(date);
    onDayClicked(date);
  };

  return (
    <div className={`r-n-cal-month-view ${styles.calendar}`}>
      <div className={`r-n-cal-weekdays ${styles.weekdays}`}>
        {calendarData.bsDays(t).map((day) => (
          <Typography key={day} className={styles.weekday} style={{ fontSize: "12px" }}>
            {day}
          </Typography>
        ))}
      </div>
      <div className={`r-n-cal-days ${styles.days}`}>
        {getDays().map(({ adDate, bsDate, bsMonth }, i) => (
          // eslint-disable-next-line jsx-a11y/no-static-element-interactions
          <div
            className={classNames("r-n-cal-day", styles.day, {
              [styles.weekend]: i % 7 === 6,
              [styles.dayMuted]: bsMonth !== viewBsMonth,
              [styles.today]: isSameDate(adDate),
              [styles.highlightedDays]: isSameWeek(adDate, highlightedDays),
              [styles.disabled]: isDateDisabled(adDate)
            })}
            key={`${bsDate} ${bsMonth}`}
            onClick={() => {
              if (!isDateDisabled(adDate)) {
                onDaySelect(adDate);
              }
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                borderRadius: "50%",
                width: "28px",
                height: "28px",
                gap: "2px"
              }}
            >
              <Typography>{calFns.toDevanagariDigits(bsDate)}</Typography>
              <Typography fontSize={8}>{moment(adDate).format("D")}</Typography>
            </Box>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MonthView;
